/**
 * Срочность заказа — `02-DATA-MODEL.md`, раздел «Автоматическая срочность».
 *
 * Отличие от демо: `today` передаётся аргументом, а не читается из модульной
 * константы. В демо (строка 462) `today` вычислялась один раз при загрузке файла —
 * в браузерном артефакте это незаметно, но в долгоживущей вкладке SPA и в
 * серверном процессе, работающем неделями, срочность бы «замерзала» на дате
 * запуска.
 */
import { daysUntil } from './dates.js';
const TONE_BY_LABEL = {
    'Готово': 'done',
    'Очень срочно': 'critical',
    'Срочно': 'urgent',
    'В работе': 'normal',
};
/**
 * Срочность заказа.
 *
 * Приоритет разрешения:
 *   1. Статус «Готово» перекрывает всё — готовый заказ не бывает срочным.
 *   2. Ручной выбор срочности (не «Авто») перекрывает вычисление.
 *   3. Иначе — вычисление по разнице между датой сдачи и сегодня:
 *      0–3 дня = «Очень срочно», ровно 4 = «Срочно», 5+ = «В работе».
 *
 * Границу «очень срочного» владелец сдвинул с двух дней до трёх (26.09.2026):
 * аппарат за три дня уже не успеть спокойно — его надо ставить в работу
 * сегодня, а не завтра. «Срочно» осталось ровно на четвёртый день: это
 * предупреждение, что срок подходит, а не сигнал бросать всё.
 *
 * Просрочка (`due` прошла, статус не «Готово») отмечается отдельным флагом,
 * а не подменяет метку: заказ может быть одновременно «Очень срочно» и просроченным,
 * и интерфейсу нужны оба факта.
 */
export function urgencyOf(order, today) {
    if (order.status === 'Готово') {
        return { label: 'Готово', tone: 'done', overdue: false };
    }
    /*
     * «Ожидание» — заказ заморожен врачом — снимает срочность НАСОВСЕМ, вместе
     * с просрочкой, и перекрывает даже выставленную вручную.
     *
     * Замороженный заказ не ждут. Дата сдачи у него осталась от прежней
     * договорённости и продолжает уходить в прошлое, поэтому без этой ветки
     * автоматическая срочность объявляла бы его сначала срочным, а потом
     * просроченным — и список краснел бы из-за работы, которую никто не ждёт.
     * Именно так и выглядел заказ, замороженный врачом на месяц.
     *
     * Ручной выбор здесь тоже не спасает: пометку «Очень срочно» ставили ДО
     * заморозки, и оставлять её — значит хранить решение, которое отменили.
     */
    if (order.status === 'Ожидание') {
        return { label: 'Ожидание', tone: 'hold', overdue: false };
    }
    const diff = daysUntil(order.due, today);
    const overdue = Number.isFinite(diff) && diff < 0;
    if (order.urgency !== 'Авто') {
        const label = order.urgency;
        return { label, tone: TONE_BY_LABEL[label] ?? 'normal', overdue };
    }
    if (!order.due || diff > 4) {
        return { label: 'В работе', tone: 'normal', overdue };
    }
    if (diff <= 3) {
        return { label: 'Очень срочно', tone: 'critical', overdue };
    }
    return { label: 'Срочно', tone: 'urgent', overdue };
}
//# sourceMappingURL=urgency.js.map